import { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderCircle, Pause, Play } from 'lucide-react';
import type { JobProcessingState } from '../shared/desktop-api';
import { CaptureDrainGateOwner } from './capture-background-drain';
import { stepClock } from './working-progress';

/** Progress events can outlive a pause. Presentation must follow admission state,
 * not infer activity from the last retained progress message. */
export function useJobProcessingState(jobId: string) {
  const [state, setState] = useState<JobProcessingState | null>(null);
  useEffect(() => {
    let live = true;
    setState(null);
    const api = window.capturePointDesktop;
    // No job, nothing to ask (the tour's start window mounts before its job exists).
    if (!jobId || !api?.getJobProcessingState || !api.onJobProcessingStateChanged) return;
    const accept = (next: JobProcessingState) => {
      if (live && next.jobId === jobId)
        setState((current) =>
          !current || current.jobId !== jobId || next.revision >= current.revision ? next : current,
        );
    };
    const unsubscribe = api.onJobProcessingStateChanged(accept);
    void api
      .getJobProcessingState(jobId)
      .then(accept)
      .catch(() => {});
    return () => {
      live = false;
      unsubscribe();
    };
  }, [jobId]);
  return state?.jobId === jobId ? state : null;
}

export function HeldSetupProgress({
  state,
  preview,
  pageCount,
  pageScope = 'sample',
}: {
  state: JobProcessingState;
  preview?: string;
  pageCount?: number;
  pageScope?: 'sample' | 'wave';
}) {
  const pausing = state.status === 'pausing';
  const queued = state.status === 'queued';
  // Pausing is still work: the step in hand stops at its next safe point and
  // what it finished is saved. Its clock moves every second so it never looks stuck.
  const [pausingSince, setPausingSince] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!pausing) {
      setPausingSince(null);
      return;
    }
    setPausingSince((since) => since ?? Date.now());
    setNow(Date.now());
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [pausing]);
  return (
    <section
      className="held-setup-progress panel"
      role="status"
      data-processing-state={state.status}
    >
      <div className="held-setup-heading">
        <Pause size={26} aria-hidden="true" />
        <div>
          <h2>
            {pausing
              ? 'Pausing processing'
              : queued
                ? 'Waiting for the other job'
                : 'Processing paused'}
          </h2>
          <p>
            {pausing
              ? 'Saving what is finished and stopping at the next safe point. No new work will start.'
              : queued
                ? 'This job will continue when the processing slot is available.'
                : 'Resume processing to continue where you left off.'}
          </p>
          {pausing && (
            <p className="held-setup-clock">Stopping · {stepClock(now - (pausingSince ?? now))}</p>
          )}
          {pageCount !== undefined && (
            <small>
              {pageCount.toLocaleString()}{' '}
              {pageScope === 'wave' ? 'pages read so far' : 'pages in this sample'}
            </small>
          )}
        </div>
      </div>
      {preview && <img className="held-setup-preview" src={preview} alt="Last page preview" />}
    </section>
  );
}

/** Prevent the renderer queue from announcing new work while main has it held.
 * Main admission remains authoritative across a screen/unmount/race. */
export function JobProcessingMonitor({ jobId }: { jobId: string }) {
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.getJobProcessingState || !api.onJobProcessingStateChanged) return;
    const owner = new CaptureDrainGateOwner(jobId, 'job processing paused');
    owner.setBlocked(true);
    let live = true;
    let revision = -1;
    const accept = (state: JobProcessingState) => {
      if (!live || state.jobId !== jobId || state.revision < revision) return;
      revision = state.revision;
      owner.setBlocked(state.pauseRequested);
    };
    const unsubscribe = api.onJobProcessingStateChanged(accept);
    void api
      .getJobProcessingState(jobId)
      .then(accept)
      .catch(() => {
        // Fail closed; the visible control reports the failed state read.
      });
    return () => {
      live = false;
      unsubscribe();
      owner.setBlocked(false);
    };
  }, [jobId]);
  return null;
}

/** The same native state and action on Jobs, setup and document review. */
export function JobProcessingButton({
  jobId,
  compact = false,
  showStatusWhenIdle = false,
}: {
  jobId: string;
  compact?: boolean;
  showStatusWhenIdle?: boolean;
}) {
  const [state, setState] = useState<JobProcessingState | null>(null);
  const [busy, setBusy] = useState(false);
  const [readPending, setReadPending] = useState(true);
  const [readError, setReadError] = useState('');
  const [actionError, setActionError] = useState('');
  const scope = useRef<{
    jobId: string;
    live: boolean;
    revision: number;
    read: number;
    command: object | null;
    state: JobProcessingState | null;
  } | null>(null);
  const read = useCallback(async () => {
    const owner = scope.current;
    if (!owner?.live || owner.jobId !== jobId) return;
    const request = ++owner.read;
    const ownsRead = () => owner.live && scope.current === owner && owner.read === request;
    setReadPending(true);
    setReadError('');
    try {
      const api = window.capturePointDesktop;
      if (!api?.getJobProcessingState || !api.onJobProcessingStateChanged)
        throw new Error('Paige could not reach processing status.');
      const next = await api.getJobProcessingState(jobId);
      if (!ownsRead()) return;
      if (next.jobId !== jobId) throw new Error('The processing status belongs to another job.');
      if (next.revision >= owner.revision) {
        owner.revision = next.revision;
        owner.state = next;
        setState(next);
      }
    } catch (reason) {
      if (ownsRead())
        setReadError(reason instanceof Error ? reason.message : 'Could not check processing.');
    } finally {
      if (ownsRead()) setReadPending(false);
    }
  }, [jobId]);
  useEffect(() => {
    const owner = {
      jobId,
      live: true,
      revision: -1,
      read: 0,
      command: null as object | null,
      state: null as JobProcessingState | null,
    };
    scope.current = owner;
    setState(null);
    setBusy(false);
    setActionError('');
    const api = window.capturePointDesktop;
    const accept = (next: JobProcessingState) => {
      if (!owner.live || next.jobId !== jobId || next.revision < owner.revision) return;
      owner.revision = next.revision;
      owner.state = next;
      owner.read++;
      setState(next);
      setReadPending(false);
      setReadError('');
    };
    const unsubscribe = api?.onJobProcessingStateChanged?.(accept);
    void read();
    return () => {
      owner.live = false;
      owner.read++;
      unsubscribe?.();
    };
  }, [jobId, read]);

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const toggle = async () => {
    const owner = scope.current;
    if (
      !owner?.live ||
      owner.jobId !== jobId ||
      !owner.state ||
      owner.state.status === 'ready' ||
      owner.command
    )
      return;
    const command = {};
    owner.command = command;
    const ownsCommand = () => owner.live && scope.current === owner && owner.command === command;
    setBusy(true);
    setActionError('');
    try {
      const next = await window.capturePointDesktop!.setJobProcessingPaused(
        jobId,
        !owner.state.pauseRequested,
      );
      if (!ownsCommand()) return;
      if (next.jobId !== jobId) throw new Error('The processing choice returned a different job.');
      if (next.revision >= owner.revision) {
        owner.revision = next.revision;
        owner.state = next;
        owner.read++;
        setState(next);
        setReadPending(false);
        setReadError('');
      }
    } catch (reason) {
      // A refused write is still an actionable error even if main publishes its
      // unchanged durable state while rejecting it. Do not treat that event as
      // a successful Pause, or let a read failure replace this command refusal.
      if (ownsCommand())
        setActionError(
          reason instanceof Error ? reason.message : 'The processing choice was not saved.',
        );
    } finally {
      if (ownsCommand()) {
        owner.command = null;
        setBusy(false);
      }
    }
  };
  const currentState = state?.jobId === jobId ? state : null;
  const pausing = currentState?.status === 'pausing';
  const paused = currentState?.pauseRequested;
  const queued = currentState?.status === 'queued';
  const detailedStatus = pausing
    ? 'Pausing · finishing current work'
    : paused
      ? 'Paused · completed documents are available'
      : queued
        ? 'Queued · another job is processing'
        : currentState?.status === 'running'
          ? 'Processing'
          : currentState?.status === 'ready' && showStatusWhenIdle
            ? 'Not processing'
            : !currentState && readPending && showStatusWhenIdle
              ? 'Checking processing'
              : '';
  const compactJobStatus = compact && showStatusWhenIdle;
  const statusLabel = compactJobStatus
    ? pausing
      ? 'Pausing · finishing work'
      : paused
        ? 'Paused'
        : queued
          ? 'Queued · waiting for turn'
          : detailedStatus
    : detailedStatus;
  if (
    (!currentState || currentState.status === 'ready') &&
    !showStatusWhenIdle &&
    !readError &&
    !actionError
  )
    return null;
  return (
    <div
      className={`job-processing-control ${paused ? 'held' : ''} ${compact ? 'compact' : ''}`}
      data-processing-state={currentState?.status ?? (readError ? 'unavailable' : 'checking')}
    >
      {currentState && currentState.status !== 'ready' && (
        <button
          type="button"
          className="secondary-button small"
          disabled={busy}
          onClick={() => void toggle()}
          aria-label={
            pausing
              ? 'Cancel pause'
              : paused
                ? 'Resume processing'
                : queued
                  ? 'Pause queued job'
                  : 'Pause processing'
          }
        >
          {busy ? (
            <LoaderCircle className="spin" size={14} />
          ) : paused ? (
            <Play size={14} />
          ) : (
            <Pause size={14} />
          )}
          {pausing
            ? 'Cancel pause'
            : compact
              ? paused
                ? 'Resume'
                : queued
                  ? 'Pause queue'
                  : 'Pause'
              : paused
                ? 'Resume processing'
                : queued
                  ? 'Pause queued job'
                  : 'Pause processing'}
        </button>
      )}
      <span role="status">{statusLabel}</span>
      {readError && (
        <>
          <small role="alert" data-telemetry="error:processing-status-failed">
            Processing status unavailable. {readError}
          </small>
          <button
            type="button"
            className="secondary-button small"
            disabled={readPending}
            onClick={() => void read()}
          >
            Retry
          </button>
        </>
      )}
      {actionError && (
        <small role="alert" data-telemetry="error:processing-pause-failed">
          {actionError}
        </small>
      )}
    </div>
  );
}
